import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { Accordion, AccordionDetails, AccordionSummary, Box, Link as MuiLink } from '@mui/material';
import { Link as RouterLink } from 'react-router';
import useBreakpoints from '../../../../hooks/useBreakpoints';
import type { NeobetFooterLink, NeobetFooterSection } from './useNeobetFooterData';
import NeobetFooterHeading from './NeobetFooterHeading';
import StyledSkeleton from '../../../atoms/StyledSkeleton';

const styles = {
  linkList: {
    display: 'grid',
    gridTemplateColumns: {
      xs: 'repeat(2, minmax(0, 1fr))',
      sm: 'repeat(3, minmax(0, 1fr))',
      md: 'repeat(5, minmax(0, 1fr))',
    },
    gap: { xs: 2, md: 3 },
  },
  section: {
    minWidth: 0,
  },
  mobileList: {
    display: 'flex',
    flexDirection: 'column',
  },
  accordion: {
    backgroundColor: 'transparent',
    backgroundImage: 'none',
    boxShadow: 'none',
    color: 'inherit',
    '&:before': {
      display: 'none',
    },
  },
  accordionSummary: {
    minHeight: 48,
    px: 0,
    py: 0.25,
    '&.Mui-expanded': {
      minHeight: 48,
    },
    '& .MuiAccordionSummary-content': {
      my: 0,
      alignItems: 'center',
    },
    '& .MuiAccordionSummary-content.Mui-expanded': {
      my: 0,
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
      color: 'neutral.50',
    },
  },
  accordionHeading: {
    mb: 0,
  },
  accordionDetails: {
    px: 0,
    pt: 0,
    pb: 1,
  },
  sectionLinks: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 0.25,
  },
  link: {
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: 28,
    px: 0,
    py: 0.125,
    textDecoration: 'none',
    typography: 'body3',
    fontWeight: 700,
    lineHeight: 1.35,
    color: 'neutral.300',
    '&:hover': {
      color: 'neutral.200',
    },
  },
  skeletonSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.75,
    minWidth: 0,
  },
  skeletonHeading: {
    width: '58%',
    height: 14,
    mb: 0.5,
  },
  skeletonLink: {
    width: '72%',
    height: 12,
  },
  skeletonMobileRow: {
    height: 48,
    width: 1,
  },
} as const;

type NeobetFooterLinksProps = {
  isLoading?: boolean;
  sections: NeobetFooterSection[];
};

const renderFooterLinkItems = (links: NeobetFooterLink[]) => (
  <Box sx={styles.sectionLinks}>
    {links.map((item) => (
      <MuiLink key={item.id} component={RouterLink} to={item.to} sx={styles.link} underline='none'>
        {item.title}
      </MuiLink>
    ))}
  </Box>
);

const SKELETON_SECTION_COUNT = 5;
const SKELETON_LINK_COUNT = 4;

const NeobetFooterLinks = ({ isLoading = false, sections }: NeobetFooterLinksProps) => {
  const { isMobile } = useBreakpoints();
  const visibleSections = sections.filter((section) => section.links.length > 0);

  if (isLoading) {
    if (isMobile) {
      return (
        <Box sx={styles.mobileList} data-testid='neobet-footer-links-loading'>
          {Array.from({ length: SKELETON_SECTION_COUNT }).map((_, index) => (
            <StyledSkeleton key={`neobet-footer-mobile-link-skeleton-${index}`} sx={styles.skeletonMobileRow} />
          ))}
        </Box>
      );
    }

    return (
      <Box sx={styles.linkList} data-testid='neobet-footer-links-loading'>
        {Array.from({ length: SKELETON_SECTION_COUNT }).map((_, sectionIndex) => (
          <Box key={`neobet-footer-link-section-skeleton-${sectionIndex}`} sx={styles.skeletonSection}>
            <StyledSkeleton sx={styles.skeletonHeading} />
            {Array.from({ length: SKELETON_LINK_COUNT }).map((_, linkIndex) => (
              <StyledSkeleton
                key={`neobet-footer-link-skeleton-${sectionIndex}-${linkIndex}`}
                sx={styles.skeletonLink}
              />
            ))}
          </Box>
        ))}
      </Box>
    );
  }

  if (isMobile) {
    return (
      <Box sx={styles.mobileList}>
        {visibleSections.map((section) => (
          <Accordion
            key={section.id}
            disableGutters
            sx={styles.accordion}
            slotProps={{
              transition: { unmountOnExit: true },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={styles.accordionSummary}>
              <NeobetFooterHeading component='span' sx={styles.accordionHeading}>
                {section.heading}
              </NeobetFooterHeading>
            </AccordionSummary>
            <AccordionDetails sx={styles.accordionDetails}>{renderFooterLinkItems(section.links)}</AccordionDetails>
          </Accordion>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={styles.linkList}>
      {visibleSections.map((section) => (
        <Box key={section.id} sx={styles.section}>
          <NeobetFooterHeading component='h2'>{section.heading}</NeobetFooterHeading>
          {renderFooterLinkItems(section.links)}
        </Box>
      ))}
    </Box>
  );
};

export default NeobetFooterLinks;
